import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { analyticsApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner } from '../components/common';
import { formatCurrency, getStageDisplayName } from '../utils/helpers';
import { stageColors, nicheConfig } from '../theme';


const Analytics = () => {
  const { settings } = useApp();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(12);

  const [mrrData, setMrrData] = useState([]);
  const [clientGrowth, setClientGrowth] = useState([]);
  const [pipelineData, setPipelineData] = useState(null);
  const [nicheData, setNicheData] = useState([]);
  const [churnData, setChurnData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [mrrRes, growthRes, pipelineRes, nicheRes, churnRes] = await Promise.all([
        analyticsApi.getMRR(timeRange),
        analyticsApi.getClientGrowth(timeRange),
        analyticsApi.getPipeline(),
        analyticsApi.getConversionByNiche(),
        analyticsApi.getChurn(6),
      ]);

      setMrrData(mrrRes.data.map(d => ({
        month: new Date(d.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        mrr: d.mrrEnd || d.mrr || 0,
        goal: settings?.mrrGoal || 2000,
      })));

      setClientGrowth(growthRes.data.map(d => ({
        month: new Date(d.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        total: d.total,
        newClients: d.newClients,
        churned: -d.churned,
        goal: settings?.clientGoal || 50,
      })));

      setPipelineData(pipelineRes.data);

      setNicheData(nicheRes.data.map(d => ({
        niche: nicheConfig[d.niche]?.label || d.niche,
        total: d.total,
        won: d.won,
        conversionRate: parseFloat(d.conversionRate).toFixed(1),
        color: nicheConfig[d.niche]?.color || '#6b7280',
      })));

      setChurnData(churnRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  return (
    <Box>
      {/* Time Range Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          select
          size="small"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          sx={{ width: 150 }}
          label="Time Range"
        >
          <MenuItem value={3}>Last 3 months</MenuItem>
          <MenuItem value={6}>Last 6 months</MenuItem>
          <MenuItem value={12}>Last 12 months</MenuItem>
        </TextField>
      </Box>

      <Grid container spacing={3}>
        {/* MRR Over Time */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Monthly Recurring Revenue
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mrrData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="mrr"
                      stroke="#2563eb"
                      strokeWidth={3}
                      dot={{ fill: '#2563eb' }}
                      name="MRR"
                    />
                    <Line
                      type="monotone"
                      dataKey="goal"
                      stroke="#10b981"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      dot={false}
                      name="Goal"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Churn Stats */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Churn Analysis
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                  <Typography variant="overline" color="error.dark">Monthly Churn Rate</Typography>
                  <Typography variant="h3" fontWeight={700} color="error.dark">
                    {churnData?.monthlyChurnRate || 0}%
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                  <Typography variant="overline" color="info.dark">Avg. Client Lifetime</Typography>
                  <Typography variant="h3" fontWeight={700} color="info.dark">
                    {churnData?.avgLifetimeMonths || 0}
                  </Typography>
                  <Typography variant="caption" color="info.dark">months</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                  <Typography variant="overline" color="text.secondary">Total Churned (6mo)</Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {churnData?.totalChurned || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Growth */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Client Growth
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="newClients" fill="#10b981" name="New Clients" />
                    <Bar dataKey="churned" fill="#ef4444" name="Churned" />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#2563eb"
                      strokeWidth={2}
                      name="Total Clients"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pipeline Conversion */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Pipeline Overview
              </Typography>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {pipelineData?.overallConversion || 0}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Overall Conversion Rate
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pipelineData?.funnel?.slice(0, 6).map((item) => (
                  <Box
                    key={item.stage}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: stageColors[item.stage],
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {getStageDisplayName(item.stage)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {item.count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({item.conversionRate}%)
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Conversion by Niche */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Conversion by Niche
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={nicheData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <YAxis dataKey="niche" type="category" width={120} />
                    <Tooltip
                      formatter={(v, name) => [
                        name === 'conversionRate' ? `${v}%` : v,
                        name === 'conversionRate' ? 'Conversion Rate' : name
                      ]}
                    />
                    <Bar dataKey="conversionRate" name="Conversion Rate">
                      {nicheData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Leads by Niche */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Leads by Niche
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={nicheData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ niche, total }) => `${niche}: ${total}`}
                      outerRadius={100}
                      dataKey="total"
                    >
                      {nicheData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
